import { Injectable } from '@angular/core';
import { UserProfileRead } from 'src/app/user/interfaces/user-profile-read';
import { UserActions } from './user.actions';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { ManagerApiService } from '../../predictor/services/manager-api-service';
import { catchError, of, tap } from 'rxjs';
import { ApiUserService } from '../../predictor/services/api-user.service';
import { UserImageRead } from '../../user/interfaces/user-image-read';
import { EnterpriseLicenseService } from 'src/app/predictor/services/enterprise-license.service';
import { LoggedInUserLicenseDetails } from 'src/app/predictor-enterprise-license/shared/interface/Enterprise-License';

export interface UserStateModel {
  userImage: string;
  userProfile: UserProfileRead | null;
  userRoles: string[];
  loggedInLicense: LoggedInUserLicenseDetails | null; 
}

const stateDefaults: UserStateModel = {
  userImage: '',
  userProfile: null,
  userRoles: [],
  loggedInLicense: null
};

export interface EnterpriseLicenseStateModel {
  loggedInLicense: LoggedInUserLicenseDetails;       // or LicenseResponse[] depending on API
  selectedLicense: LoggedInUserLicenseDetails | null;
  totalRecords: number;
  loadingLicenses: boolean;
  continuationTokens: string[];
  pageIndex: number;
  pageSize: number;
}


export interface TcAcceptanceStateModel {
  loading: boolean;
  accepted: boolean | null;   // null = not loaded yet
  error: string | null;
}
``


@State<UserStateModel>({
  name: 'User',
  defaults: stateDefaults,
})

@State<TcAcceptanceStateModel>({
  name: 'tcAcceptance',
  defaults: {
    loading: false,
    accepted: null,
    error: null
  }
})

@Injectable()
export class UserState {
  constructor(private readonly managerApiService: ManagerApiService, private readonly apiUserService: ApiUserService, private readonly licenseService : EnterpriseLicenseService) {}

  @Selector()
  static userProfile(UserStateModel: UserStateModel): any | null {
    return {
      name: `${UserStateModel.userProfile?.firstName} ${UserStateModel.userProfile?.lastName}`,
      image: UserStateModel.userImage,
      email: UserStateModel.userProfile?.email
    };
  }
  
  @Selector()
  static userImage(UserStateModel: UserStateModel): string {
    return UserStateModel.userImage;
  }

   @Selector()
  static userProfileName(UserStateModel: UserStateModel): any {
    return {
      firstName: UserStateModel.userProfile?.firstName,
      lastName: UserStateModel.userProfile?.lastName,
      email: UserStateModel.userProfile?.email
    };
  }


  @Selector()
  static manageFeatureAccess(UserStateModel: UserStateModel): boolean {
    return UserStateModel.userRoles.includes('Area-Based Admin');
  }

  @Selector()
  static dfpSpecialistRoleAccess(UserStateModel: UserStateModel): boolean {
    return UserStateModel.userRoles.includes('DFP Specialist');
  }
  
  @Selector()
  static debuggerAccess(UserStateModel: UserStateModel): boolean {
    return UserStateModel.userRoles.includes('Debugger');
  }

  @Action(UserActions.SetUserFeatureAccess)
  setUserFeatureAccess(ctx: StateContext<UserStateModel>) {
    return this.managerApiService.getEnabledFeatures().pipe(
      tap((featureAccess: string[]) => {
        ctx.patchState({ userRoles: featureAccess });
      })
    );
  }

  @Action(UserActions.SetUserProfileInformation)
  setUserProfileInformation(ctx: StateContext<UserStateModel>) {
    return this.apiUserService.getUserProfileInformation().pipe(
      tap((userProfile: any) => {
        const profile: UserProfileRead = { firstName: userProfile.firstName, lastName: userProfile.lastName, email: userProfile.email};
        ctx.patchState({ userProfile: profile, userRoles: userProfile?.roles.$values});
      })
    );
  }

  @Action(UserActions.SetUserImage)
  setUserImage(ctx: StateContext<UserStateModel>) {
    return this.apiUserService.getUserImage().pipe(
      tap((userImage: UserImageRead) => {
        ctx.patchState({ userImage: userImage?.image });
      })
    );
  }


   @Selector()
  static enterpriseLicenseAdminAccessEnabled(UserStateModel: UserStateModel): boolean {
    return UserStateModel.userRoles.includes('Enterprise License Admin');
  }

    @Selector()
  static manageLicensePageAccess(UserStateModel: UserStateModel): boolean {
      const allowedRoles = ['Area-Based Admin', 'Enterprise License Admin'];
return UserStateModel.userRoles.some(role => allowedRoles.includes(role));

  }

  @Selector()
static userLicenseAccess(state: EnterpriseLicenseStateModel): LoggedInUserLicenseDetails {
  return state.loggedInLicense;
  }


  @Selector()
  static userTcAccepted(state: TcAcceptanceStateModel): boolean {
    return state.accepted ?? false;
  }



 @Action(UserActions.GetLicenseAccess)
getLoggedInUserLicense(ctx: StateContext<EnterpriseLicenseStateModel>) {
  ctx.patchState({ loadingLicenses: true });

  return this.licenseService.getLoggedInUserLicense().pipe(
    tap((license: LoggedInUserLicenseDetails) => {
 ctx.patchState({ loggedInLicense : license })
    }),
    catchError(err => {
      console.error('Failed to fetch logged-in user license', err);
      ctx.patchState({
        selectedLicense: null,
        loadingLicenses: false
      });
      return of(null); // prevent breaking the action stream
    })
  );
}




}
